<template>
  <a-card id="photo-and-video">
    <h4>{{ $t('product.photo_video') }}</h4>
    <div class="text-muted mb-4">
      {{ $t('product.foto_video_description_master', { minWidth, minHeight }) }}
    </div>
    
    <a-form-model-item prop="images" class="mb-0">
      <a-upload
        v-for="(image, index) in images" 
        ref="upload"
        :key="index"
        :name="`avatar${index}`"
        accept=".jpg,.jpeg,.png"
        list-type="picture-card"
        class="image-master"
        :show-upload-list="false"
        :custom-request="uploadImage"
        :transform-file="transformFile"
        :before-upload="beforeUpload"
        :disabled="isLoading"
        @change="onChange"
      >
        <div v-if="image.preview" class="image-container">
          <img :src="image.preview" alt="avatar" class="image-file" />
          <div class="image-overlay">
            <a-button 
              v-if="index != 0" 
              type="primary" 
              class="mb-2" 
              :style="{ marginTop: index == 1 ? '35px' : '15px' }" 
              :disabled="isLoading"
              @click="$event => onSetMain($event, index)"
            >
              {{ $t('product.set_main') }}
            </a-button>
            <a-button 
              v-if="index != 1" 
              type="primary" 
              class="mb-2" 
              ghost 
              :style="{ marginTop: index == 0 ? '35px' : '0px' }" 
              :disabled="isLoading"
              @click="$event => onSetThumbnail($event, index)"
            >
              {{ $t('product.set_thumbnail') }}
            </a-button>
            <a-popconfirm
              :title="$t('product.confirm_delete_photo')"
              :ok-text="$t('fulfillment.confirm')"
              :cancel-text="$t('fulfillment.cancel')"
              @confirm="$event => onRemoveImage($event, index)"
            >
              <a-button v-if="!isLoading" type="primary" ghost @click="e => e.stopPropagation()">
                {{ $t('product.delete_photo') }}
              </a-button>
            </a-popconfirm>
          </div>
        </div>
        <div v-else>
          <a-icon :type="loading ? 'loading' : 'plus'" />
          <div class="ant-upload-text">
            {{ $t('product.product_image') }}
          </div>
        </div>
        <span v-if="image.preview && index < 2" class="image-badge">{{ index == 0 ? $t('product.main') : $t('product.thumbnail') }}</span>
      </a-upload>
    </a-form-model-item>
  </a-card>
</template>

<script>
import { uploadProductImage } from '@/api/product'

export default {
  name: 'PhotoAndVideo',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    load: {
      type: Boolean,
      default: false,
    },
    businessId: {
      type: String,
      default: '',
    },
    isTriggerUpload: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      minWidth: 300,
      minHeight: 300,
    };
  },
  computed: {
    images: {
      get() {
        return !this.value.length
          ? [{ file: '' }]
          : this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    isLoading() {
      return this.load
    },
    idBusiness() {
      return this.businessId
    },
  },
  watch: {
    isTriggerUpload(value) {
      if (value) this.triggerUpload()
    },
  },
  methods: {
    async uploadImage({ file, onSuccess, onError }) {
      setTimeout(() => {
        onSuccess({ status: 'done' })
      }, 1000)
    },
    async transformFile(file, maxSizeInMB = 1) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        
        reader.onload = function(event) {
          const img = new Image()
          img.src = event.target.result
          
          img.onload = function() {
            const canvas = document.createElement('canvas')
            let width = img.width
            let height = img.height
            
            if (width > height) {
              if (width > 1920) {
                height = Math.round((height * 1920) / width)
                width = 1920
              }
            } else {
              if (height > 1920) {
                width = Math.round((width * 1920) / height)
                height = 1920
              }
            }
            
            canvas.width = width
            canvas.height = height
            
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)
            
            let quality = 0.9
            let compression = canvas.toDataURL('image/jpeg', quality)
            
            while (compression.length > maxSizeInMB * 1024 * 1024 && quality > 0.1) {
              quality -= 0.1
              compression = canvas.toDataURL('image/jpeg', quality)
            }
            
            fetch(compression)
              .then(res => res.blob())
              .then(blob => {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                })
                resolve(compressedFile)
              })
              .catch(err => reject(err))
          }
          
          img.onerror = function() {
            reject(new Error('Failed to load image'))
          }
        }
        
        reader.onerror = function() {
          reject(new Error('Failed to read file'))
        }
      })
    },
    async onChange({ file }) {
      if (file.status === 'uploading') {
        this.loading = true
        return
      } else if (file.status === 'done') {
        const previewFile = URL.createObjectURL(file.originFileObj)
        const tempFileList = [].concat(this.images)
        tempFileList.splice(-1)
        tempFileList.push({ ...file, preview: previewFile })
        
        if (tempFileList.length < 15) {
          tempFileList.push({ file: '' })
        }

        this.images = tempFileList
        this.loading = false
        this.$message.success(`${file.name} file uploaded successfully.`)
        this.$emit('validate', 'images')
      } else if (file.status === 'error') {
        this.$message.error(`${file.name} file upload failed.`)
      }
    },
    triggerUpload() {
      const inputElement = document.querySelector('input[type="file"]')
      if (inputElement) inputElement.click()
      this.$emit('upload', false)
    },
    beforeUpload(file) {
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        this.$message.error('Image must smaller than 10MB!')
        return false
      }

      return isLt10M
    },
    onSetMain(event, index) {
      const tempFile = this.onRemoveImage(event, index)
      this.images.unshift(tempFile)
    },
    onSetThumbnail(event, index) {
      const tempFile = this.onRemoveImage(event, index)
      this.images.splice(1, 0, tempFile)
    },
    onRemoveImage(event, index) {
      event.stopPropagation()
      const tempFile = JSON.parse(JSON.stringify(this.images[index]))
      this.images.splice(index, 1)
      return tempFile
    },
  },
}
</script>

<style lang="scss">
.image-container {
  position: relative;
  width: 140px;
  height: 140px;
}

.image-badge {
  position: absolute;
  left: 0;
  bottom: 0;
  padding: 2px 8px;
  font-size: 12px;
  color: #fff;
  background-color: #2196f3;
  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
}

.image-file {
  opacity: 1;
  display: block;
  width: 140px;
  height: 140px;
  object-fit: contain;
  border-radius: 8px;
  transition: .5s ease;
  backface-visibility: hidden;
}

.image-overlay {
  width: 140px;
  height: 140px;
  transition: .5s ease;
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -ms-transform: translate(-50%, -50%);
  text-align: center;

  .ant-btn {
    width: 110px !important;
    padding: 0 11px !important;
    font-size: 12px !important;
  }
}

.image-container:hover {
  .image-file {
    opacity: 0.3;
  }

  .image-overlay {
    opacity: 1;
  }
}

.image-master {
  display: inline-block !important;
  width: 140px !important;
  height: 140px !important;
  margin-right: 12px;
  margin-bottom: 12px;

  .ant-upload-select-picture-card {
    width: 140px !important;
    height: 140px !important;
    background-color: #fff !important;
    border: 1px solid #e4e9f0 !important;
    border-radius: 8px !important;

    &:hover {
      background-color: #fafafa !important;
    }

    i {
      font-size: 32px;
      color: #999;
    }

    .ant-upload-text {
      margin-top: 8px;
      color: #999;
    }
  }
  
  .ant-upload {
    position: relative !important;
    padding: 0 !important;
  }
}
</style>
